import React from 'react';
import { useLocation, useNavigate } from 'react-router';
import noop from 'lodash/noop';

import { routes } from '@shared/routes';

import { addQuery, parseQuery } from '../../helpers/query';
import { generateRouter } from '../../routing/routing';
import { ContextProps } from './RoutingContext.types';

export const RoutingContext = React.createContext<ContextProps>({
  router: generateRouter(() => {}, routes),
  location: undefined,
  go: noop,
});

export const RoutingProvider: React.FC<React.PropsWithChildren> = ({ children }) => {

  const navigate = useNavigate();
  const location = useLocation();
  const redirect = React.useMemo(() => new URLSearchParams(location.search).get('redirect') || '', [location.search]);

  const go = React.useCallback(
    (pathname: string, params?: CVT.Routing.RouteParams, replace = false) => {
      const existing = parseQuery(window.location.search);
      const finalParams = { ...existing, ...params };

      if (redirect) {
        finalParams.redirect = redirect;
      }

      navigate(
        { pathname, search: addQuery('', finalParams) },
        { replace },
      );
    },
    [navigate, redirect],
  );

  const router = React.useMemo(() => generateRouter(go, routes), [go]);

  return (
    <RoutingContext.Provider value={{
      router,
      location,
      go: (params, replace) => go(window.location.pathname, params, replace),
    }}>
      {children}
    </RoutingContext.Provider>
  );
};

export const RoutingConsumer = RoutingContext.Consumer;
